// @flow
import React, { Component, type Node } from 'react';
import {
  DragDropContext,
  Droppable,
  Draggable,
  type DropResult,
  // $FlowFixMe
} from 'react-beautiful-dnd';
import { type FieldProps } from 'redux-form';
import { FormattedMessage, injectIntl, type IntlShape } from 'react-intl';

import type { FieldDefinition, IngestConfigField } from 'state/types';
import ManagedUploadField from './ManagedUploadField';
import Field from './Field';
import localizedStrings from 'util/localizedStrings';
import { displayNameForField } from 'util/fieldsPageUtils';
import {
  ALL_KNOWN_READ_ONLY_FIELD_DEF_NAMES,
  getAllDisplayableFields,
} from 'portfolio-shared/util/fieldDefUtils';

type Props = {
  disabled?: boolean,
  fieldDefs: FieldDefinition[],
  intl: IntlShape,
} & FieldProps;

const FIELD_DEFS_ID = 'fieldDefinitions';
const FORM_FIELDS_ID = 'formFields';
const DROPPABLE_TABLE_STYLE = {
  display: 'flex',
  flexDirection: 'column',
  width: '45%',
};

function TableHeader(props: { children?: Node }) {
  return (
    <div className="columns is-marginless has-text-weight-bold is-size-7">
      {props.children}
    </div>
  );
}

class ManagedUploadFields extends Component<Props> {
  getListClass: Function;
  handleDragEnd: Function;
  handleRequiredClick: Function;
  moveFieldToFormFields: Function;
  moveFieldFromFormFields: Function;

  constructor() {
    super();

    this.getListClass = this.getListClass.bind(this);
    this.handleDragEnd = this.handleDragEnd.bind(this);
    this.handleRequiredClick = this.handleRequiredClick.bind(this);
    this.moveFieldFromFormFields = this.moveFieldFromFormFields.bind(this);
    this.moveFieldToFormFields = this.moveFieldToFormFields.bind(this);
  }

  moveFieldToFormFields(
    fdId: string,
    destinationIndex = Number.MAX_SAFE_INTEGER
  ) {
    const {
      fieldDefs,
      input: { onChange, value },
    } = this.props;

    const newValue = Array.from(value);
    newValue.splice(destinationIndex, 0, {
      fieldDefinition: fieldDefs.find(fd => fd.id === fdId),
      required: false,
    });
    onChange(newValue);
  }

  moveFieldFromFormFields(fdId: string) {
    const {
      input: { onChange, value },
    } = this.props;

    onChange(
      value.filter((f: IngestConfigField) => f.fieldDefinition.id !== fdId)
    );
  }

  handleDragEnd(result: DropResult) {
    const {
      input: { onChange, value },
    } = this.props;
    const { source, destination } = result;

    if (!destination) {
      return;
    }

    if (source.droppableId === destination.droppableId) {
      if (source.droppableId === FIELD_DEFS_ID) {
        return;
      }

      // reorder
      const newValue: IngestConfigField[] = Array.from(value);
      const [removed] = newValue.splice(source.index, 1);
      newValue.splice(destination.index, 0, removed);
      onChange(newValue);
    } else if (destination.droppableId === FIELD_DEFS_ID) {
      this.moveFieldFromFormFields(result.draggableId);
    } else if (destination.droppableId === FORM_FIELDS_ID) {
      this.moveFieldToFormFields(result.draggableId, destination.index);
    }
  }

  getItemStyle(isDragging: boolean, draggableStyle: Object) {
    const spacing = 2;
    return {
      fontSize: '14px',
      userSelect: 'none',
      padding: spacing,
      margin: `0 0 ${spacing}px 0`,
      ...draggableStyle,
    };
  }

  getListStyle(isDraggingOver: boolean) {
    return {
      border: 'solid 1px var(--borderColor)',
      outline: isDraggingOver ? 'solid 2px var(--darkGrey)' : '',
      padding: 8,
      height: 350,
      overflowY: 'auto',
      overflowX: 'hidden',
    };
  }

  getListClass() {
    const { disabled } = this.props;
    return `${
      disabled ? 'has-background-light has-text-grey' : 'has-text-grey-darker'
    }`;
  }

  handleRequiredClick(id: string) {
    const {
      input: { onChange, value },
    } = this.props;

    onChange(
      value.map((icf: IngestConfigField) =>
        icf.fieldDefinition.id === id
          ? {
              ...icf,
              required: !icf.required,
            }
          : icf
      )
    );
  }

  render() {
    const {
      disabled,
      fieldDefs,
      input: { value },
      intl,
    } = this.props;
    return (
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <DragDropContext onDragEnd={this.handleDragEnd}>
          <div style={DROPPABLE_TABLE_STYLE} data-test-name="available-fields">
            <TableHeader>
              <div className="column">
                <FormattedMessage
                  {...localizedStrings.CATALOG_INGEST_FIELDS_LABEL}
                />
              </div>
            </TableHeader>
            <Droppable droppableId={FIELD_DEFS_ID}>
              {(provided, snapshot) => (
                <div
                  className={this.getListClass()}
                  ref={provided.innerRef}
                  style={this.getListStyle(snapshot.isDraggingOver)}
                >
                  {getAllDisplayableFields(fieldDefs)
                    .filter(
                      fd =>
                        !ALL_KNOWN_READ_ONLY_FIELD_DEF_NAMES.includes(
                          fd.systemName
                        ) &&
                        (Array.isArray(value) &&
                          !value.find(v => v.fieldDefinition.id === fd.id))
                    )
                    .sort((a, b) =>
                      displayNameForField(intl, a.systemName).localeCompare(
                        displayNameForField(intl, b.systemName)
                      )
                    )
                    .map((fd, idx) => (
                      <Draggable
                        key={fd.id}
                        draggableId={fd.id}
                        index={idx}
                        isDragDisabled={disabled}
                      >
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            style={this.getItemStyle(
                              snapshot.isDragging,
                              provided.draggableProps.style
                            )}
                            data-test-name={'available-field-' + fd.systemName}
                          >
                            <Field
                              fieldDef={fd}
                              onDoubleClick={this.moveFieldToFormFields}
                            />
                          </div>
                        )}
                      </Draggable>
                    ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </div>

          <div style={DROPPABLE_TABLE_STYLE} data-test-name="selected-fields">
            <TableHeader>
              <div className="column is-one-quarter">
                <FormattedMessage
                  {...localizedStrings.CATALOG_INGEST_REQUIRED_LABEL}
                />
              </div>
              <div className="column is-three-quarters">
                <FormattedMessage
                  {...localizedStrings.CATALOG_INGEST_FIELDS_FOR_UPLOAD_FORM_LABEL}
                />
              </div>
            </TableHeader>
            <Droppable droppableId={FORM_FIELDS_ID}>
              {(provided, snapshot) => (
                <div
                  className={this.getListClass()}
                  ref={provided.innerRef}
                  style={this.getListStyle(snapshot.isDraggingOver)}
                >
                  {Array.isArray(value) &&
                    value.map((fd, idx) => (
                      <Draggable
                        isDragDisabled={disabled}
                        key={fd.fieldDefinition.id}
                        draggableId={fd.fieldDefinition.id}
                        index={idx}
                      >
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            style={this.getItemStyle(
                              snapshot.isDragging,
                              provided.draggableProps.style
                            )}
                            data-test-name={
                              'selected-field-' + fd.fieldDefinition.systemName
                            }
                          >
                            <ManagedUploadField
                              disabled={disabled}
                              field={fd}
                              onDoubleClick={this.moveFieldFromFormFields}
                              onRequiredClick={this.handleRequiredClick}
                            />
                          </div>
                        )}
                      </Draggable>
                    ))}
                  {Array.isArray(value) && value.length === 0 && (
                    <div
                      className="has-text-centered has-text-grey is-size-7"
                      style={{ margin: '40% 8px', userSelect: 'none' }}
                    >
                      <FormattedMessage
                        {...localizedStrings.CATALOG_INGEST_DRAG_HINT_TEXT}
                      />
                    </div>
                  )}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </div>
        </DragDropContext>
      </div>
    );
  }
}

export default injectIntl(ManagedUploadFields);



// WEBPACK FOOTER //
// ./src/view/Pages/CatalogsPage/ManagedFields/CatalogManagedUploadFields.js